<template>
  <!-- The frame only (T10): section structure, keyboard/mouse affordances and
       responsive behaviour. T12 fills OUR SELECTION with real matches, T11 wires
       the CTA to the external marketplace search. -->
  <div class="search-autocomplete" v-if="open" role="listbox">
    <div class="sa-section">
      <div class="sa-section-title">OUR SELECTION</div>

      <div v-if="loading" class="sa-empty">Searching…</div>

      <div v-else-if="results.length === 0" class="sa-empty">
        No matches in our selection yet.
      </div>

      <a
        v-for="(result, index) in results"
        :key="result.id"
        class="sa-result"
        :class="{ 'is-active': index === activeIndex }"
        href="#"
        role="option"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        @mouseenter="activeIndex = index"
        @click.prevent="$emit('select', result)"
      >
        <span class="sa-result-name">{{ result.name }}</span>
        <span v-if="result.brand" class="sa-result-brand">{{ result.brand }}</span>
      </a>
    </div>

    <!-- Full-width CTA pinned to the bottom of the dropdown. The label carries
         the user's raw input verbatim — no parsing, no brand stripping (T11). -->
    <button type="button" class="sa-cta" @click="$emit('search-external', query)">
      <span class="sa-cta-label">
        Search '<span class="sa-cta-query">{{ query }}</span>'<span class="sa-cta-scope"> in Asian Marketplaces</span>
      </span>
      <span class="sa-cta-arrow" aria-hidden="true">&rarr;</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'SearchAutocomplete',
  props: {
    // The raw, untrimmed-in-spirit input. Passed straight through to the CTA.
    query: { type: String, default: '' },
    open: { type: Boolean, default: false },
    // T12 supplies these; until then the section renders its empty state.
    results: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      activeIndex: -1
    };
  },
  watch: {
    // A changed query invalidates whatever row was highlighted.
    query() {
      this.activeIndex = -1;
    },
    open(isOpen) {
      if (!isOpen) this.activeIndex = -1;
    }
  }
};
</script>

<style scoped>
.search-autocomplete {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1002;
  overflow: hidden;
}

.sa-section {
  padding: 8px 0;
  max-height: 320px;
  overflow-y: auto;
}

.sa-section-title {
  padding: 8px 16px 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #888;
  text-transform: uppercase;
}

.sa-empty {
  padding: 10px 16px 14px;
  font-size: 0.88rem;
  color: #999;
}

.sa-result {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 9px 16px;
  color: #1a1a1a;
  font-size: 0.9rem;
  text-decoration: none;
}

.sa-result.is-active {
  background: #f4f7fb;
  color: #1b4b8f;
}

.sa-result-brand {
  color: #777;
  font-size: 0.82rem;
}

.sa-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-top: 1px solid #eee;
  background: #000;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}

.sa-cta:hover {
  background: #1b4b8f;
}

/* The label is the flexible half of the CTA; the arrow keeps its intrinsic
   width. Without `min-width: 0` the ellipsis on the query below can never
   engage, because a flex item defaults to its content's minimum size. */
.sa-cta-label {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.sa-cta-query {
  /* Long inputs must not blow out the CTA width. */
  max-width: 55%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* On mobile the search bar is a fixed full-width overlay, so the dropdown
   hangs off the bottom of that overlay rather than the nav row. */
@media (max-width: 768px) {
  .search-autocomplete {
    left: 15px;
    right: 15px;
    margin-top: 2px;
  }

  .sa-section {
    max-height: 50vh;
  }
}

/* Below this width "Search 'query' in Asian Marketplaces" can no longer sit on
   one line, and wrapping it broke the CTA's layout. The scope phrase is the
   droppable half — the quoted query is what the user needs to see. */
@media (max-width: 420px) {
  .sa-cta-scope {
    display: none;
  }

  .sa-cta-query {
    max-width: 70%;
  }
}
</style>
